// ---------------------------------------------- modules import
import EditorJS from "@editorjs/editorjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FirebaseReducer,
  isEmpty,
  isLoaded,
  useFirestoreConnect
} from "react-redux-firebase";
import { useHistory } from "react-router-dom";

import { tools } from "./common";
import firebase from "../../../config/fbInit";
import { AppState } from "../../../store/rootReducer";
import { IUserData, IOrderedPost } from "../../../store/firebase/common";
import PermissionHelper, {
  MenuIndex
} from "../../../store/helpers/permissionHelper";
import TagHelper from "../../../store/helpers/tagHelper";
import ValidationHelper, {
  pattern
} from "../../../store/helpers/validationHelper";
import {
  IPost,
  IReader,
  newPost,
  PostActionType
} from "../../../store/post/common";
import {
  thunkAddPost,
  thunkPinPost,
  thunkUpdatePost,
  thunkDeletePost,
  thunkEditorError
} from "../../../store/post/thunkActions";

// ---------------------------------------------- constant categories
const categories = [
  "human resource",
  "operational",
  "administration",
  "report",
  "development"
];

// ---------------------------------------------- reduce ordered data
const reducePosts = (
  orderedPosts: IOrderedPost[],
  profile: FirebaseReducer.Profile<IUserData>
) =>
  orderedPosts
    .reduce(
      (currentPosts, orderedPost) => [...currentPosts, { ...orderedPost }],
      [] as IPost[]
    )
    .reduce(
      (currentPosts, post) =>
        TagHelper.isUserTagged(post.tags, profile.tags)
          ? [...currentPosts, { ...post }]
          : [...currentPosts],
      [] as IPost[]
    );

// ---------------------------------------------- custom hooks
export const useAuth = () => {
  // ---------------------------------------------- redux connect
  const auth = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).auth
  );
  const profile = useSelector(
    (state: AppState) =>
      (state.firebase as FirebaseReducer.Reducer<
        Record<string, Record<string | number, string | number>>,
        IUserData
      >).profile
  );

  // ---------------------------------------------- local state
  const [areLoaded, setAreLoaded] = useState(
    isLoaded(auth) && isLoaded(profile)
  );
  const [isEmptied, setIsEmptied] = useState(isEmpty(auth) || isEmpty(profile));

  // ---------------------------------------------- effects
  useEffect(() => {
    setAreLoaded(isLoaded(auth) && isLoaded(profile));
    setIsEmptied(isEmpty(auth) || isEmpty(profile));
  }, [auth, profile]);

  return {
    auth,
    profile,
    areLoaded,
    isEmptied
  };
};

export const usePosts = (
  index: MenuIndex,
  profile: FirebaseReducer.Profile<IUserData>
) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const reducerPath = PermissionHelper.getReducer(index);

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({
    collection: collectionPath,
    orderBy: ["lastModified", "desc"]
  });

  // ---------------------------------------------- redux connect
  const orderedPosts = useSelector(
    (state: AppState) =>
      state.firestore.ordered[collectionPath] as IOrderedPost[]
  );
  const loading = useSelector((state: AppState) => state[reducerPath].loading);
  const error = useSelector((state: AppState) => state[reducerPath].error);

  // ---------------------------------------------- local state
  const [posts, setPosts] = useState<IPost[]>([]);
  const [filtered, setFiltered] = useState<IPost[]>([]);
  const [fetching, setFetching] = useState(true);
  const [criteria, setCriteria] = useState({
    prop: "title",
    keywords: "",
    category: null
  });

  // ---------------------------------------------- handlers
  const handleFilter = (criterion: string, value: string | null) =>
    setCriteria({
      ...criteria,
      [criterion]:
        criterion === "category" ? (value === "all" ? null : value) : value
    });

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedPosts)) {
      if (!isEmpty(orderedPosts)) {
        const currentPosts = reducePosts(orderedPosts, profile);

        setPosts(currentPosts);
      } else {
        setPosts([]);
      }

      setFetching(false);
    } else {
      setFetching(true);
    }
  }, [orderedPosts, profile]);

  useEffect(() => {
    const { prop, keywords, category } = criteria;
    let filtered = posts;

    if (category !== null)
      filtered = filtered.filter(post =>
        RegExp(`${category}`, "i").test(post.category)
      );

    setFiltered(
      filtered.filter(post =>
        RegExp(`${keywords}`, "i").test(
          prop === "author" ? post[prop].name : post[prop]
        )
      )
    );
  }, [criteria, posts]);

  // ---------------------------------------------- return value
  return {
    loading,
    error,
    filtered,
    fetching,
    criteria,
    categories,
    onFilter: handleFilter
  };
};

export const usePostForDetails = (
  index: MenuIndex,
  profile: FirebaseReducer.Profile<IUserData>,
  id?: string
) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const dispatch = useDispatch();

  // ---------------------------------------------- router state
  let history = useHistory();

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({ collection: collectionPath });

  // ---------------------------------------------- redux connect
  const orderedPosts = useSelector(
    (state: AppState) =>
      state.firestore.ordered[collectionPath] as IOrderedPost[]
  );

  // ---------------------------------------------- local state
  const [post, setPost] = useState<IPost>(newPost());

  // ---------------------------------------------- handlers
  const handleUpdateReaders = (
    profile: FirebaseReducer.Profile<IUserData>,
    readers: IReader[]
  ) => {
    if (profile)
      dispatch(
        thunkUpdatePost(
          {
            ...post,
            readers
          },
          collectionPath
        )
      );
  };

  const handlePin = (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (auth.uid && PermissionHelper.getModify(profile.permissions, index)) {
      dispatch(
        thunkPinPost({
          ...post,
          lastModified: firebase.firestore.Timestamp.now(),
          pinnedAt: firebase.firestore.Timestamp.now(),
          indexOrigin: index
        })
      );
      history.push(path);
    }
  };

  const handleDelete = (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (auth.uid && PermissionHelper.getModify(profile.permissions, index)) {
      dispatch(thunkDeletePost(post.id, collectionPath));
      history.push(path);
    }
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedPosts) && !isEmpty(orderedPosts)) {
      const currentPosts = reducePosts(orderedPosts, profile);

      const currentPost = currentPosts.find(
        currentPost => currentPost.id === id
      );

      if (currentPost) setPost(currentPost);
    }
  }, [orderedPosts, profile, id]);

  // ---------------------------------------------- return value
  return {
    post,
    onUpdateReaders: handleUpdateReaders,
    onPin: handlePin,
    onDelete: handleDelete
  };
};

export const usePostForAddForm = (index: MenuIndex) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const dispatch = useDispatch();

  // ---------------------------------------------- router state
  let history = useHistory();

  // ---------------------------------------------- local state
  const [post, setPost] = useState<IPost>(newPost());
  const [editor, setEditor] = useState<EditorJS | undefined>(undefined);
  const [isReady, setIsReady] = useState(false);

  const isPostValid = () =>
    ValidationHelper.validate(post.title, pattern.post.title) &&
    post.category.length > 0;

  // ---------------------------------------------- handlers
  const handleChange = (prop: string, value: string) =>
    setPost({
      ...post,
      [prop]:
        prop === "tags"
          ? TagHelper.toggleTag(post.tags, parseInt(value))
          : value
    });

  const handleAdd = async (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (
      auth.uid &&
      PermissionHelper.getModify(profile.permissions, index) &&
      editor
    )
      await editor
        .save()
        .then(data => {
          dispatch(
            thunkAddPost(
              {
                ...post,
                author: {
                  email: profile.email,
                  name: profile.name
                },
                data,
                readers: [
                  { id: auth.uid, name: profile.name, email: profile.email }
                ]
              },
              collectionPath
            )
          );
          history.push(path);
        })
        .catch(error =>
          dispatch(
            thunkEditorError({
              type: PostActionType.ADD_POST_ERROR,
              payload: { error }
            })
          )
        );
  };

  // ---------------------------------------------- effects
  useEffect(
    () =>
      setEditor(
        new EditorJS({
          holder: "codex-editor",
          tools,
          placeholder: "Let`s write an awesome post today!",
          onReady: () => {
            setIsReady(true);
          }
        })
      ),
    []
  );

  // ---------------------------------------------- return value
  return {
    post,
    isReady,
    isPostValid,
    categories,
    onChange: handleChange,
    onAdd: handleAdd
  };
};

export const usePostForUpdateForm = (
  index: MenuIndex,
  profile: FirebaseReducer.Profile<IUserData>,
  id?: string
) => {
  const collectionPath = PermissionHelper.getCollection(index);
  const dispatch = useDispatch();

  // ---------------------------------------------- router state
  let history = useHistory();

  // ---------------------------------------------- firestore connect
  useFirestoreConnect({ collection: collectionPath });

  // ---------------------------------------------- redux connect
  const orderedPosts = useSelector(
    (state: AppState) =>
      state.firestore.ordered[collectionPath] as IOrderedPost[]
  );

  // ---------------------------------------------- local state
  const [post, setPost] = useState<IPost>(newPost());
  const [editor, setEditor] = useState<EditorJS | undefined>(undefined);
  const [isReady, setIsReady] = useState(false);

  const isPostValid = () =>
    ValidationHelper.validate(post.title, pattern.post.title) &&
    post.category.length > 0;

  // ---------------------------------------------- handlers
  const handleChange = (prop: string, value: string) =>
    setPost({
      ...post,
      [prop]:
        prop === "tags"
          ? TagHelper.toggleTag(post.tags, parseInt(value))
          : value
    });

  const handleUpdate = async (
    auth: FirebaseReducer.AuthState,
    profile: FirebaseReducer.Profile<IUserData>,
    path: string
  ) => {
    if (
      auth.uid &&
      PermissionHelper.getModify(profile.permissions, index) &&
      editor
    )
      await editor
        .save()
        .then(data => {
          dispatch(
            thunkUpdatePost(
              {
                ...post,
                data,
                lastModified: firebase.firestore.Timestamp.now()
              },
              collectionPath
            )
          );
          history.push(path);
        })
        .catch(error =>
          dispatch(
            thunkEditorError({
              type: PostActionType.UPDATE_POST_ERROR,
              payload: { error }
            })
          )
        );
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    if (isLoaded(orderedPosts) && !isEmpty(orderedPosts)) {
      const currentPosts = reducePosts(orderedPosts, profile);

      const currentPost = currentPosts.find(
        currentPost => currentPost.id === id
      );

      if (currentPost) setPost(currentPost);
    }
  }, [orderedPosts, profile, id]);

  useEffect(() => {
    if (!editor && post.id) {
      setEditor(
        new EditorJS({
          holder: "codex-editor",
          tools,
          data: post.data,
          onReady: () => {
            setIsReady(true);
          }
        })
      );
    }
  }, [editor, post.id, post.data]);

  // ---------------------------------------------- return value
  return {
    post,
    editor,
    isReady,
    isPostValid,
    categories,
    onChange: handleChange,
    onUpdate: handleUpdate
  };
};
